import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast, Slide } from 'react-toastify';
import { FiCheckSquare, FiZap, FiArrowRight, FiX } from 'react-icons/fi';
import { fetchMyTasks } from '../../store/slices/tasksSlice';
import { canAccessRoute } from '../../utils/permissions';
import { localizeSeeded } from '../../pages/FlowManagement/flowUtils';
import './ApprovalNotifier.scss';

// Dedicated toast container for approval "push" notifications. Kept separate
// from the app-wide top-right container (index.js) so these can live at the
// bottom-right, stack independently, and carry richer, clickable content.
const CONTAINER_ID = 'approval-notifications';
const POLL_MS = 30000;

// Modern push-card rendered inside a toast. Clicking anywhere (except the
// close button) navigates to the contract so the approver can act.
function ApprovalCard({ approval, t, onOpen, closeToast }) {
  const { i18n } = useTranslation();
  const contract = approval.contractId || {};
  const title = contract.contractName || contract.counterpartyName || contract.contractNumber || t('approvals.unknownContract');
  const go = () => { onOpen(contract._id); closeToast(); };
  return (
    <div
      className="apn"
      role="button"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } }}
    >
      <span className="apn__icon"><FiCheckSquare /></span>
      <div className="apn__body">
        <span className="apn__eyebrow">{t('approvals.notify.title')}</span>
        <span className="apn__contract">{title}</span>
        <div className="apn__meta">
          {contract.contractNumber && <span className="apn__num">{contract.contractNumber}</span>}
          {approval.stepName && <span className="apn__step"><FiZap /> {localizeSeeded(approval.stepName, i18n.language)}</span>}
        </div>
        <span className="apn__cta">{t('approvals.notify.cta')} <FiArrowRight /></span>
      </div>
      <button
        type="button"
        className="apn__close"
        onClick={(e) => { e.stopPropagation(); closeToast(); }}
        aria-label={t('common.close')}
      >
        <FiX />
      </button>
    </div>
  );
}

function ApprovalNotifier() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((s) => s.auth);
  const canView = !!user && canAccessRoute(user.role, '/approvals', user.effectivePermissions || null);

  // Set of approval ids we've already accounted for. `null` until the first
  // fetch seeds a baseline — pre-existing approvals must NOT fire a toast, only
  // ones that arrive while the platform is open.
  const seenRef = useRef(null);
  // Keep the latest navigate/t in refs so the toast closures stay fresh.
  const navRef = useRef(navigate);
  navRef.current = navigate;
  const tRef = useRef(t);
  tRef.current = t;

  const openContract = useCallback((contractId) => {
    if (contractId) navRef.current(`/contracts/${contractId}`);
  }, []);

  const dispatch = useDispatch();
  const approvals = useSelector((s) => s.tasks.approvals);
  const tasksLoaded = useSelector((s) => s.tasks.loaded);

  // Diff each new task list against the ids already seen. The first list
  // after (re)gaining access only seeds the baseline, silently.
  useEffect(() => {
    if (!isAuthenticated || !canView || !tasksLoaded) return;
    const currentIds = new Set(approvals.map((a) => a._id));
    if (seenRef.current === null) {
      seenRef.current = currentIds;
      return;
    }

    // Fire one push per newly-arrived approval.
    approvals.forEach((approval) => {
      if (seenRef.current.has(approval._id)) return;
      seenRef.current.add(approval._id);
      toast(
        ({ closeToast }) => (
          <ApprovalCard approval={approval} t={tRef.current} onOpen={openContract} closeToast={closeToast} />
        ),
        {
          containerId: CONTAINER_ID,
          toastId: `approval-${approval._id}`, // dedupe if it's still on screen
        }
      );
    });

    // Drop ids that are no longer pending so a re-raised step can notify again.
    seenRef.current.forEach((id) => { if (!currentIds.has(id)) seenRef.current.delete(id); });
  }, [approvals, tasksLoaded, isAuthenticated, canView, openContract]);

  // The notifier is the one steady poller of the shared task list; the
  // dashboard widget and the sidebar badge read whatever it last fetched.
  useEffect(() => {
    if (!isAuthenticated || !canView) {
      seenRef.current = null; // reset baseline when logged out / access revoked
      return undefined;
    }
    dispatch(fetchMyTasks());
    const refresh = () => dispatch(fetchMyTasks({ force: true }));
    const interval = setInterval(refresh, POLL_MS);
    // Refresh immediately when the user returns to the tab.
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', refresh);
    };
  }, [isAuthenticated, canView, dispatch]);

  if (!canView) return null;

  return (
    <ToastContainer
      containerId={CONTAINER_ID}
      position="bottom-right"
      transition={Slide}
      autoClose={12000}
      newestOnTop
      closeOnClick={false}
      closeButton={false}
      hideProgressBar={false}
      pauseOnHover
      pauseOnFocusLoss={false}
      draggable
      theme="light"
      icon={false}
      className="apn-container"
      toastClassName="apn-toast"
    />
  );
}

export default ApprovalNotifier;
